import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ExamGroupsTab from "./ExamGroupsTab";
import AdvancedExamSheet from "./AdvancedExamSheet";
import MarksGradeTab from "./MarksGradeTab";
import AdvanceOptionsTab from "./AdvanceOptionsTab";
import { ClipboardList, Plus, Pencil, Trash2, Loader2, X, Sparkles, Calendar, Search, Check } from "lucide-react";
import { toast } from "sonner";

const TERMS = ["PT-1", "Half-Yearly", "PT-2", "Annual", "Term 1", "Term 2", "Unit Test 1", "Unit Test 2", "Final"];
const empty = { name: "", term: "Term 1", class_id: "", class_ids: [], exam_date: "", subjects: [], status: "Active" };

export default function Exams() {
  const { user } = useAuth();
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState(empty);
  const [sameTiming, setSameTiming] = useState(false);
  const [sameMarks, setSameMarks] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState("");
  const [tab, setTab] = useState("exams");
  const [advOpen, setAdvOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [termFilter, setTermFilter] = useState("all");
  const [selected, setSelected] = useState([]);
  const canManage = hasPerm(user, "manage_exams");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/exams").then((r) => setExams(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/sessions").then((r) => {
      setSessions(r.data);
      const active = r.data.find((s) => s.status === "Active") || r.data[0];
      if (active) setSession(active.name);
    }).catch(() => toast.error("Could not load academic sessions"));
  }, [load]);

  const openNew = () => { setEdit(null); setF(empty); setSameTiming(false); setSameMarks(false); setOpen(true); };
  const openEdit = (ex) => { setEdit(ex); setSameTiming(false); setSameMarks(false); setF({ name: ex.name, term: ex.term || "Term 1", class_id: ex.class_id, class_ids: [ex.class_id], exam_date: ex.exam_date || "", subjects: ex.subjects || [], status: ex.status || "Active" }); setOpen(true); };

  const loadSubjectsFor = async (cid) => {
    try {
      const r = await api.get(`/classes/${cid}/subjects`);
      const subs = (r.data.subjects || []).map((s) => ({ subject_id: s.id, subject_name: s.name, max_marks: 100, pass_marks: 33, eval_type: "marks", date: "", day: "", start_time: "", end_time: "" }));
      setF((p) => ({ ...p, subjects: subs }));
      if (subs.length === 0) toast.info("This class has no subjects assigned yet. Assign them in the Subjects tab.");
    } catch (e) { /* ignore */ }
  };

  const toggleClassSel = (cid) => {
    const prevAnchor = f.class_ids[0];
    const next = f.class_ids.includes(cid) ? f.class_ids.filter((x) => x !== cid) : [...f.class_ids, cid];
    const newAnchor = next[0] || "";
    setF((p) => ({ ...p, class_ids: next, class_id: newAnchor }));
    if (!newAnchor) { setF((p) => ({ ...p, subjects: [] })); return; }
    if (newAnchor !== prevAnchor) loadSubjectsFor(newAnchor);
  };

  const onClassChange = async (cid) => {
    setF((p) => ({ ...p, class_id: cid, class_ids: [cid] }));
    try {
      const r = await api.get(`/classes/${cid}/subjects`);
      const subs = (r.data.subjects || []).map((s) => ({ subject_id: s.id, subject_name: s.name, max_marks: 100, pass_marks: 33, eval_type: "marks", date: "", day: "", start_time: "", end_time: "" }));
      setF((p) => ({ ...p, class_id: cid, class_ids: [cid], subjects: subs }));
      if (subs.length === 0) toast.info("This class has no subjects assigned yet. Assign them in the Subjects tab.");
    } catch (e) { /* ignore */ }
  };
  const updSub = (i, k, v) => setF((p) => ({ ...p, subjects: p.subjects.map((s, idx) => {
    if (sameMarks && k === "max_marks") return { ...s, [k]: v };
    if (sameTiming && (k === "start_time" || k === "end_time")) return { ...s, [k]: v };
    return idx === i ? { ...s, [k]: v } : s;
  }) }));
  const updDate = (i, v) => {
    const day = v ? new Date(v + "T00:00:00").toLocaleDateString("en-US", { weekday: "long" }) : "";
    setF((p) => ({ ...p, subjects: p.subjects.map((s, idx) => idx === i ? { ...s, date: v, day } : s) }));
  };
  const toggleSameMarks = (checked) => {
    setSameMarks(!!checked);
    if (checked) setF((p) => {
      const first = p.subjects[0];
      if (!first) return p;
      return { ...p, subjects: p.subjects.map((s) => ({ ...s, max_marks: first.max_marks })) };
    });
  };
  const toggleSameTiming = (checked) => {
    setSameTiming(!!checked);
    if (checked) setF((p) => {
      const first = p.subjects.find((s) => s.start_time || s.end_time) || p.subjects[0];
      if (!first) return p;
      return { ...p, subjects: p.subjects.map((s) => ({ ...s, start_time: first.start_time || "", end_time: first.end_time || "" })) };
    });
  };
  const removeSub = (i) => setF((p) => ({ ...p, subjects: p.subjects.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!f.name.trim()) { toast.error("Exam name required"); return; }
    const targets = edit ? [f.class_id] : f.class_ids;
    if (!targets || targets.length === 0) { toast.error("Select at least one class"); return; }
    if (f.subjects.length === 0) { toast.error("Add at least one subject"); return; }
    const subjects = f.subjects.map((s) => ({ ...s, max_marks: Number(s.max_marks) || 0, pass_marks: Number(s.pass_marks) || 33 }));
    try {
      if (edit) {
        const cls = classes.find((c) => c.id === f.class_id);
        await api.put(`/exams/${edit.id}`, { name: f.name, term: f.term, class_id: f.class_id, exam_date: f.exam_date, status: f.status, academic_session: cls?.academic_session || "", subjects });
        toast.success("Exam saved");
      } else {
        for (const cid of targets) {
          const cls = classes.find((c) => c.id === cid);
          await api.post("/exams", { name: f.name, term: f.term, class_id: cid, exam_date: f.exam_date, status: f.status, academic_session: cls?.academic_session || "", subjects });
        }
        toast.success(`Created ${targets.length} exam${targets.length === 1 ? "" : "s"}`);
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const remove = async (ex) => {
    if (!window.confirm(`Delete exam "${ex.name}"? All entered marks for it will be removed.`)) return;
    try { await api.delete(`/exams/${ex.id}`); toast.success("Exam deleted"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const bulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Delete ${selected.length} selected exam${selected.length === 1 ? "" : "s"}? All entered marks will be removed.`)) return;
    try {
      await Promise.all(selected.map((id) => api.delete(`/exams/${id}`)));
      toast.success(`Deleted ${selected.length} exam${selected.length === 1 ? "" : "s"}`);
      setSelected([]); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const toggleOne = (id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const termOptions = Array.from(new Set(exams.map((e) => e.term).filter(Boolean)));
  const filteredExams = exams.filter((e) =>
    (termFilter === "all" || e.term === termFilter) &&
    (!search.trim() || (e.name || "").toLowerCase().includes(search.trim().toLowerCase()))
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2 flex-wrap"><ClipboardList className="text-indigo-500" size={26} />Examinations
            {session && <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-2.5 py-0.5 text-xs font-semibold align-middle" data-testid="current-session-badge"><Calendar size={12} />Session {session}</span>}
          </h1>
          <p className="text-slate-500">Manage exam groups, exams, grading and marksheet settings.</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 shadow-sm">
          <Calendar size={16} className="text-indigo-500" />
          <span className="text-xs font-medium text-slate-500">Academic Session</span>
          <Select value={session} onValueChange={setSession}>
            <SelectTrigger className="w-36 h-9" data-testid="exam-session-select"><SelectValue placeholder="Session" /></SelectTrigger>
            <SelectContent>{sessions.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}{s.status === "Active" ? " (Active)" : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="exam-tabs">
          <TabsTrigger value="groups" data-testid="tab-groups">Groups</TabsTrigger>
          <TabsTrigger value="exams" data-testid="tab-exams">Exams</TabsTrigger>
          <TabsTrigger value="grades" data-testid="tab-grades">Marks &amp; Grade Range</TabsTrigger>
          <TabsTrigger value="advanced" data-testid="tab-advanced">Advance Options</TabsTrigger>
        </TabsList>

        <TabsContent value="groups" className="mt-4">
          <ExamGroupsTab session={session} />
        </TabsContent>

        <TabsContent value="exams" className="mt-4 space-y-4">
          <div className="flex justify-end gap-2">
            {canManage && <Button variant="outline" onClick={() => setAdvOpen(true)} className="gap-1.5" data-testid="add-adv-exam-btn"><Sparkles size={16} />Advanced Exam</Button>}
            {canManage && <Button onClick={openNew} className="gap-1.5" data-testid="add-exam-btn"><Plus size={16} />Create Exam</Button>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9 h-9" placeholder="Search exams by name…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="exam-search" />
            </div>
            <Select value={termFilter} onValueChange={setTermFilter}>
              <SelectTrigger className="w-48 h-9" data-testid="exam-term-filter"><SelectValue placeholder="All groups/terms" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Groups / Terms</SelectItem>
                {termOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            {canManage && selected.length > 0 && (
              <Button variant="destructive" onClick={bulkDelete} className="gap-1.5 ml-auto" data-testid="bulk-delete-exams-btn"><Trash2 size={15} />Delete ({selected.length})</Button>
            )}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
          filteredExams.length === 0 ? <div className="py-16 text-center text-slate-400"><ClipboardList size={40} className="mx-auto mb-2 opacity-40" />{exams.length === 0 ? "No exams created yet." : "No exams match your search/filter."}</div> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  {canManage && <th className="px-4 py-2.5 w-10"><Checkbox checked={filteredExams.length > 0 && selected.length === filteredExams.length} onCheckedChange={(c) => setSelected(c ? filteredExams.map((e) => e.id) : [])} data-testid="exam-select-all" /></th>}
                  {["Exam", "Term", "Class", "Date", "Subjects", "Students", ""].map((h) => <th key={h} className="px-4 py-2.5 text-left">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredExams.map((ex) => (
                  <tr key={ex.id} className={`hover:bg-slate-50 ${selected.includes(ex.id) ? "bg-indigo-50/50" : ""}`} data-testid={`exam-row-${ex.id}`}>
                    {canManage && <td className="px-4 py-3"><Checkbox checked={selected.includes(ex.id)} onCheckedChange={() => toggleOne(ex.id)} data-testid={`exam-select-${ex.id}`} /></td>}
                    <td className="px-4 py-3 font-medium text-slate-800">{ex.name}</td>
                    <td className="px-4 py-3 text-slate-500">{ex.term}</td>
                    <td className="px-4 py-3 text-slate-500">{ex.class_name}</td>
                    <td className="px-4 py-3 text-slate-500">{ex.exam_date || "—"}</td>
                    <td className="px-4 py-3 text-slate-500">{ex.subjects?.length || 0}</td>
                    <td className="px-4 py-3 text-slate-500">{ex.student_count}</td>
                    <td className="px-4 py-3 text-right">
                      {canManage && <div className="flex gap-1 justify-end">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(ex)} data-testid={`edit-exam-${ex.id}`}><Pencil size={15} /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500" onClick={() => remove(ex)} data-testid={`delete-exam-${ex.id}`}><Trash2 size={15} /></Button>
                      </div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          </div>
        </TabsContent>

        <TabsContent value="grades" className="mt-4">
          <MarksGradeTab session={session} sessions={sessions} />
        </TabsContent>

        <TabsContent value="advanced" className="mt-4">
          <AdvanceOptionsTab session={session} sessions={sessions} />
        </TabsContent>
      </Tabs>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-none sm:w-[50vw] overflow-y-auto flex flex-col" data-testid="exam-dialog">
          <SheetHeader><SheetTitle>{edit ? "Edit Exam" : "Create Exam"}</SheetTitle></SheetHeader>
          <div className="space-y-4 mt-4 flex-1">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Exam Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. First Term Exam" data-testid="exam-name-input" className="mt-1" /></div>
              <div><Label>Term</Label>
                <Select value={f.term} onValueChange={(v) => setF({ ...f, term: v })}><SelectTrigger className="mt-1" data-testid="exam-term-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TERMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="sm:col-span-2"><Label>Class{!edit && <span className="text-slate-400 font-normal"> (select one or more)</span>}</Label>
                {edit ? (
                  <Select value={f.class_id} onValueChange={onClassChange}><SelectTrigger className="mt-1" data-testid="exam-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
                    <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.section}</SelectItem>)}</SelectContent></Select>
                ) : (
                  <div className="mt-1.5 flex flex-wrap gap-2" data-testid="exam-class-multiselect">
                    {classes.length === 0 ? <span className="text-xs text-slate-400">No classes found.</span> : classes.map((c) => {
                      const on = f.class_ids.includes(c.id);
                      return (
                        <button key={c.id} type="button" onClick={() => toggleClassSel(c.id)} data-testid={`exam-class-chip-${c.id}`}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${on ? "bg-indigo-600 border-indigo-600 text-white" : "border-slate-300 text-slate-600 hover:border-indigo-400"}`}>
                          {on && <Check size={14} />}{c.name} {c.section}
                        </button>
                      );
                    })}
                  </div>
                )}
                {!edit && f.class_ids.length > 1 && <p className="text-[11px] text-slate-400 mt-1.5">Subjects & schedule below are taken from <b>{classes.find((c) => c.id === f.class_ids[0])?.name}</b> and applied to all {f.class_ids.length} selected classes (one exam created per class).</p>}
              </div>
              <div><Label>Exam Date</Label><Input type="date" value={f.exam_date} onChange={(e) => setF({ ...f, exam_date: e.target.value })} className="mt-1" /></div>
            </div>

            <div>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label>Subjects & Schedule</Label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                    <Checkbox checked={sameMarks} onCheckedChange={toggleSameMarks} data-testid="same-marks-checkbox" />
                    Same Marks for All
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                    <Checkbox checked={sameTiming} onCheckedChange={toggleSameTiming} data-testid="same-timing-checkbox" />
                    Same Timing for All
                  </label>
                </div>
              </div>
              <div className="mt-2 space-y-2">
                {f.subjects.length === 0 && <div className="rounded-lg border border-slate-200 px-3 py-4 text-sm text-slate-400">Select a class to load its subjects.</div>}
                {f.subjects.map((s, i) => (
                  <div key={s.subject_id} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`exam-subject-row-${i}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <span className="font-medium text-sm text-slate-800">{s.subject_name}</span>
                      <div className="flex items-center gap-2">
                        <div className="inline-flex rounded-md border border-slate-200 p-0.5" data-testid={`exam-evaltype-${i}`}>
                          <button type="button" onClick={() => updSub(i, "eval_type", "marks")} className={`px-2.5 py-1 text-xs rounded ${(s.eval_type || "marks") === "marks" ? "bg-indigo-500 text-white" : "text-slate-500 hover:text-slate-700"}`} data-testid={`exam-evaltype-marks-${i}`}>Marks</button>
                          <button type="button" onClick={() => updSub(i, "eval_type", "grade")} className={`px-2.5 py-1 text-xs rounded ${s.eval_type === "grade" ? "bg-indigo-500 text-white" : "text-slate-500 hover:text-slate-700"}`} data-testid={`exam-evaltype-grade-${i}`}>Grade</button>
                        </div>
                        {(s.eval_type || "marks") === "marks" && (<>
                          <Label className="text-xs text-slate-500 whitespace-nowrap">Max Marks</Label>
                          <Input type="number" value={s.max_marks} onChange={(e) => updSub(i, "max_marks", e.target.value)} className="h-8 w-20" data-testid={`exam-max-${i}`} />
                        </>)}
                        <button onClick={() => removeSub(i)} className="text-rose-400 hover:text-rose-600" data-testid={`exam-remove-${i}`}><X size={16} /></button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div><Label className="text-xs text-slate-500">Date</Label><Input type="date" value={s.date || ""} onChange={(e) => updDate(i, e.target.value)} className="h-9 mt-1" data-testid={`exam-date-${i}`} /></div>
                      <div><Label className="text-xs text-slate-500">Day</Label><Input value={s.day || ""} readOnly placeholder="Auto" className="h-9 mt-1 bg-slate-50" data-testid={`exam-day-${i}`} /></div>
                      <div><Label className="text-xs text-slate-500">Timing</Label>
                        <div className="flex items-center gap-1 mt-1">
                          <Input type="time" value={s.start_time || ""} onChange={(e) => updSub(i, "start_time", e.target.value)} className="h-9" data-testid={`exam-start-${i}`} />
                          <span className="text-slate-400">-</span>
                          <Input type="time" value={s.end_time || ""} onChange={(e) => updSub(i, "end_time", e.target.value)} className="h-9" data-testid={`exam-end-${i}`} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <SheetFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-exam-btn">Save Exam</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AdvancedExamSheet open={advOpen} onOpenChange={setAdvOpen} classes={classes} session={session} onSaved={load} />
    </div>
  );
}
